/**
 * Suno音乐生成节点 - 使用Suno AI生成音乐
 * 
 * 功能说明：
 * 1. 接收歌词文本节点（prompt）
 * 2. 配置音乐参数（风格标签、是否纯音乐、标题等）
 * 3. 调用 Suno 音乐生成 API
 * 4. 创建音频任务节点展示结果
 * 
 * 连接说明：
 * - 输入：文本输入节点（歌词）- 必需
 * - 输出：音频任务节点
 */
import React, { memo, useState, useEffect, useCallback, useMemo } from 'react';
import { useReactFlow, useStore, Handle, Position } from '@xyflow/react';
import { nanoid } from 'nanoid';
import { useProject } from '../providers/ProjectProvider';
import { useNodeOperations } from '../providers/NodeOperationsProvider';
import LocalDataService from '../../../api/LocalDataService';
import ApiService from '../../../api/ApiService';
import { toast } from 'react-toastify';
import { TaskPricePreview } from './TaskPricePreview';
import { useTaskQuote } from './useTaskQuote';
import './SunoMusicNode.css';

// Suno API 配置
const SUNO_APP_NAME = '7576286497131135010';
const SUNO_TASK_TYPE = 'coze_v2_api';
const apiService = new ApiService();

// 常用风格标签预设
const STYLE_PRESETS = [
  { id: 'pop', label: '流行', tags: 'pop, upbeat, catchy' },
  { id: 'rock', label: '摇滚', tags: 'rock, energetic, guitar' },
  { id: 'folk', label: '民谣', tags: 'folk, acoustic, emotional' },
  { id: 'electronic', label: '电子', tags: 'electronic, EDM, synth' },
  { id: 'jazz', label: '爵士', tags: 'jazz, smooth, saxophone' },
  { id: 'classical', label: '古典', tags: 'classical, orchestral, piano' },
  { id: 'hiphop', label: '说唱', tags: 'hip hop, rap, beat' },
  { id: 'rnb', label: 'R&B', tags: 'r&b, soul, smooth' },
];

export const SunoMusicNode = memo(({ id, data }) => {
  const { updateNodeData, getNode } = useReactFlow();
  const { addNodesWithEdges, duplicateNode, createNodeAndConnect } = useNodeOperations();
  const edges = useStore((state) => state.edges);
  const canvasNodes = useStore((state) => state.nodes);
  const { readOnly } = useProject();
  
  // 基础配置
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  
  // 音乐参数
  const [makeInstrumental, setMakeInstrumental] = useState(data.makeInstrumental || false);
  const [tags, setTags] = useState(data.tags || '');
  const [negativeTags, setNegativeTags] = useState(data.negativeTags || '');
  const [title, setTitle] = useState(data.title || '');
  const [selectedPreset, setSelectedPreset] = useState(data.selectedPreset || '');
  
  // 高级设置
  const [showAdvanced, setShowAdvanced] = useState(data.showAdvanced || false);

  // 同步节点数据
  useEffect(() => {
    updateNodeData(id, {
      makeInstrumental,
      tags,
      negativeTags,
      title,
      selectedPreset,
      showAdvanced,
    });
  }, [id, makeInstrumental, tags, negativeTags, title, selectedPreset, showAdvanced, updateNodeData]);

  const connectedLyrics = useMemo(() => {
    const edge = edges.find(item => item.target === id && item.targetHandle === 'lyrics');
    return canvasNodes.find(node => node.id === edge?.source)?.data?.prompt || null;
  }, [canvasNodes, edges, id]);

  const inputParams = useMemo(() => ({
    prompt: (connectedLyrics || '').trim(),
    make_instrumental: makeInstrumental,
    tags: tags.trim(),
    negative_tags: negativeTags.trim(),
    title: title.trim(),
  }), [connectedLyrics, makeInstrumental, negativeTags, tags, title]);

  const quoteEnabled = Boolean(
    connectedLyrics?.trim().length >= 1 && connectedLyrics.trim().length <= 5000
  ) && !readOnly;
  const taskQuote = useTaskQuote({
    enabled: quoteEnabled,
    apiVersion: 'v2',
    appName: SUNO_APP_NAME,
    taskType: SUNO_TASK_TYPE,
    params: inputParams,
  });

  // 验证连接
  const isValidConnection = useCallback((connection) => {
    if (connection.targetHandle === 'lyrics') {
      const sourceNode = getNode(connection.source);
      return sourceNode?.type === 'promptInput';
    }
    return true;
  }, [getNode]);

  // 处理 Handle 标签点击 - 自动创建节点并连接
  const handleHandleLabelClick = useCallback((e, handleId, handleType) => {
    e.stopPropagation();
    
    if (readOnly) {
      toast.info('只读模式下无法创建节点');
      return;
    }
    
    createNodeAndConnect(id, 'sunoMusic', handleId, handleType);
  }, [id, readOnly, createNodeAndConnect]);

  // 选择预设风格
  const handlePresetSelect = (presetId) => {
    if (readOnly) return;
    
    const preset = STYLE_PRESETS.find(p => p.id === presetId);
    if (preset) {
      setSelectedPreset(presetId);
      setTags(preset.tags);
    }
  };

  // 生成音乐
  const handleGenerate = async () => {
    try {
      setError(null);
      setIsGenerating(true);
      
      // 1. 获取歌词
      const lyrics = connectedLyrics;
      if (!lyrics || lyrics.trim().length < 1) {
        throw new Error('请连接歌词文本节点');
      }
      
      if (lyrics.trim().length > 5000) {
        throw new Error('歌词最多5000个字符');
      }
      
      // 2. 获取用户 token
      const userData = LocalDataService.load_user_data();
      if (!userData || !userData.token) {
        throw new Error('请先登录');
      }
      
      console.log('Suno音乐生成参数:', inputParams);

      const confirmedQuote = await taskQuote.quoteNow();
      // 4. 创建任务
      const response = await fetch(`${apiService.apiBase}/v2/tasks/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token: userData.token,
          user_type: 1,
          app_name: SUNO_APP_NAME,
          task_type: SUNO_TASK_TYPE,
          input_params: inputParams
        })
      });
      
      const result = await response.json();
      
      if (result.code === 200 && result.data) {
        const taskData = result.data;
        toast.success('Suno音乐生成任务已创建！');
        
        // 5. 创建任务节点
        const currentNode = getNode(id);
        const taskNodeId = nanoid();
        const taskNode = {
          id: taskNodeId,
          type: 'audioTask',
          position: {
            x: currentNode.position.x + 450,
            y: currentNode.position.y,
          },
          data: {
            taskId: taskData.task_id,
            taskType: 'suno-music',
            status: taskData.status || 'pending',
            inputParams: inputParams,
            chargedPoints: Number(taskData.money ?? confirmedQuote.points),
          },
        };
        
        // 6. 创建连接边
        const newEdge = {
          id: nanoid(),
          source: id,
          target: taskNodeId,
          type: 'default',
        };
        
        // 7. 添加节点和边
        await addNodesWithEdges([taskNode], [newEdge]);
        
        setIsGenerating(false);
      } else {
        throw new Error(result.message || '创建任务失败');
      }
      
    } catch (err) {
      console.error('Suno音乐生成失败:', err);
      const errorMessage = err?.message || '生成失败，请重试';
      setError(errorMessage);
      toast.error(errorMessage);
      setIsGenerating(false);
    }
  };

  // 复制节点
  const handleDuplicate = (e) => {
    e.stopPropagation();
    duplicateNode(id);
  };

  return (
    <div className="SunoMusicNode-container">
      {/* 复制按钮 */}
      <button 
        className="SunoMusicNode-duplicate-button"
        onClick={handleDuplicate}
        title="复制节点"
      >
        <i className="bi bi-copy"></i>
      </button>

      {/* 歌词输入 Handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="lyrics"
        className="SunoMusicNode-handle"
        style={{ top: '50%' }}
        isValidConnection={isValidConnection}
      >
        <div 
          className="SunoMusicNode-handle-label SunoMusicNode-handle-label-clickable"
          onClick={(e) => handleHandleLabelClick(e, 'lyrics', 'text')}
          title="点击自动创建文本输入节点"
        >
          歌词
        </div>
      </Handle>

      {/* 输出 Handle */}
      <Handle
        type="source"
        position={Position.Right}
        className="SunoMusicNode-handle SunoMusicNode-handle-output"
      />

      <div className="SunoMusicNode-title">Suno音乐生成</div>

      <div className="SunoMusicNode-wrapper">
        {/* 歌词输入状态 */}
        <div className="SunoMusicNode-field">
          <div className="SunoMusicNode-label-with-hint">
            <label className="SunoMusicNode-label">歌词</label>
            <span className="SunoMusicNode-label-hint">最多5000字符</span>
          </div>
          {connectedLyrics ? (
            <div className="SunoMusicNode-connected-text">
              {connectedLyrics.substring(0, 80)}
              {connectedLyrics.length > 80 ? '...' : ''}
            </div>
          ) : (
            <div className="SunoMusicNode-placeholder">
              🎤 未连接文本输入节点
            </div>
          )}
        </div>

        {/* 音乐类型 */}
        <div className="SunoMusicNode-field">
          <label className="SunoMusicNode-label">音乐类型</label>
          <div className="SunoMusicNode-button-group">
            <button
              className={`SunoMusicNode-type-button ${!makeInstrumental ? 'SunoMusicNode-type-button-active' : ''}`}
              onClick={() => setMakeInstrumental(false)}
              disabled={readOnly}
            >
              🎤 带人声
            </button>
            <button
              className={`SunoMusicNode-type-button ${makeInstrumental ? 'SunoMusicNode-type-button-active' : ''}`}
              onClick={() => setMakeInstrumental(true)}
              disabled={readOnly}
            >
              🎹 纯音乐
            </button>
          </div>
        </div>

        {/* 风格预设 */}
        <div className="SunoMusicNode-field">
          <label className="SunoMusicNode-label">风格预设</label>
          <div className="SunoMusicNode-preset-grid">
            {STYLE_PRESETS.map((preset) => (
              <button
                key={preset.id}
                className={`SunoMusicNode-preset-button ${selectedPreset === preset.id ? 'SunoMusicNode-preset-button-active' : ''}`}
                onClick={() => handlePresetSelect(preset.id)}
                disabled={readOnly}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>

        {/* 标题输入 */}
        <div className="SunoMusicNode-field">
          <label className="SunoMusicNode-label">音乐标题（可选）</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="输入音乐标题..."
            disabled={readOnly || isGenerating}
            className="SunoMusicNode-input"
          />
        </div>

        {/* 高级设置开关 */}
        <div className="SunoMusicNode-advanced-toggle">
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            disabled={readOnly}
            className="SunoMusicNode-toggle-button"
          >
            {showAdvanced ? '▼' : '▶'} 高级设置
          </button>
        </div>

        {/* 高级设置面板 */}
        {showAdvanced && (
          <div className="SunoMusicNode-advanced">
            {/* 自定义风格标签 */}
            <div className="SunoMusicNode-field">
              <div className="SunoMusicNode-label-with-hint">
                <label className="SunoMusicNode-label">风格标签</label>
                <span className="SunoMusicNode-label-hint">最多1000字符</span>
              </div>
              <input
                type="text"
                value={tags}
                onChange={(e) => {
                  setTags(e.target.value);
                  setSelectedPreset(''); // 清除预设选择
                }}
                placeholder="如: pop, upbeat, energetic"
                disabled={readOnly || isGenerating}
                className="SunoMusicNode-input"
              />
            </div>

            {/* 排除风格标签 */}
            <div className="SunoMusicNode-field">
              <label className="SunoMusicNode-label">排除风格（可选）</label>
              <input
                type="text"
                value={negativeTags}
                onChange={(e) => setNegativeTags(e.target.value)}
                placeholder="如: heavy, aggressive, loud"
                disabled={readOnly || isGenerating}
                className="SunoMusicNode-input"
              />
            </div>
          </div>
        )}

        <TaskPricePreview quote={taskQuote} />

        {/* 生成按钮 */}
        <button
          onClick={handleGenerate}
          disabled={isGenerating || readOnly || (quoteEnabled && taskQuote.status !== 'success')}
          className={`SunoMusicNode-generate ${
            isGenerating || readOnly || (quoteEnabled && taskQuote.status !== 'success')
              ? 'SunoMusicNode-generate-disabled'
              : ''
          }`}
        >
          {isGenerating ? '创建中...' : '🎵 生成音乐'}
        </button>

        {/* 费用提示 */}
        {/* <div className="SunoMusicNode-cost-hint">
          💰 每次生成消耗约60积分，可生成2首音乐
        </div> */}

        {/* 错误提示 */}
        {error && (
          <div className="SunoMusicNode-error">
            ❌ {error}
          </div>
        )}
      </div>
    </div>
  );
});

SunoMusicNode.displayName = 'SunoMusicNode';
